import { Drawer } from "antd";
import { useForm } from "antd/es/form/Form";
import i18n from "i18next";
import { type FC, useState } from "react";
import { useDispatch } from "react-redux";

import { TerminalBatchAddForm } from "src/app/features/Terminal/components/forms/TerminalsBatchAddForm";
import { useTerminalBatchAddMutation } from "src/app/features/Terminal/services/server/TerminalApi";
import type { TerminalBatchAddEntity } from "src/app/features/Terminal/types/TerminalBatchAddEntity";
import { SecondaryButton } from "../../../../../components/Buttons/components/ActionButton";
import { showSuccessNotification, showWarningNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";

export const TerminalBatchAddButton: FC = () => {
    const [open, setOpen] = useState(false);
    const [form] = useForm();

    const showDrawer = () => setOpen(true);
    const onClose = () => setOpen(false);

    const dispatch = useDispatch();
    const [saveFormData] = useTerminalBatchAddMutation();

    const onSearch = async (filters: TerminalBatchAddEntity) => {
        try {
            const result = await saveFormData({ data: filters });
            const hasError = "error" in result;
            const hasImportErrors: string[] = result?.data?.importErrors ?? [];

            if (hasImportErrors.length) {
                const translatedErrors = hasImportErrors
                    .slice(0, 6) // cap to max 6, safe for shorter arrays
                    .map((key) => i18n.t(key))
                    .filter((msg) => !!msg && msg.trim().length > 0);

                dispatch(
                    showWarningNotification({
                        text: translatedErrors.length > 0 ? translatedErrors.join("\n") : i18n.t("common.genericImportError"),
                        autoCloseDisabled: true,
                    }),
                );
                onClose();
                return;
            }

            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.batchAddSuccess", {
                            0: `${result?.data?.iptSuccessCount}`,
                            1: `${result?.data?.optSuccessCount}`,
                            2: `${result?.data?.unattendedSuccessCount}`,
                            3: `${result?.data?.androidSuccessCount}`,
                            4: `${result?.data?.ecommerceSuccessCount}`,
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                onClose();
                form.resetFields();
            }
        } catch {
            // Error handling is managed by RTK Query error in result
        }
    };

    return (
        <>
            <SecondaryButton onClick={showDrawer} disabled={false}>
                {i18n.t("list.addBatchUpdate")}
            </SecondaryButton>
            <Drawer
                title={i18n.t("add.batch.update.terminals.header")}
                closable={{ "aria-label": "Close Button" }}
                onClose={onClose}
                open={open}
            >
                <TerminalBatchAddForm form={form} searchCallback={onSearch} />
            </Drawer>
        </>
    );
};
